// @ts-nocheck
import { useEffect, useState, useCallback, useRef } from 'react';
import { Slide, Box } from '@mui/material';
import { X } from '@phosphor-icons/react';
import { t } from 'i18next';
import * as PropTypes from 'prop-types';
import './LibraryNewServiceSlide.css';
import { getLibraryService } from '../../../../services/library/service';
import {
  initServiceTypeDayFormData,
  getDefaultFieldOptions,
  setServiceTypeDayFormData
} from '../../../../utils/serviceTypes/serviceTypes';
import ServiceTypesForm from '../../DetailedItinerary/ServiceTypesForm/ServiceTypesForm';

const initialDaysData = {};

/**
 * Functional React component for adding/updating a library service content.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {boolean} [props.openServiceSlide] - Open slide state.
 * @param {function} [props.handleServiceSlideClose] - Function to handle open slide state.
 * @param {...any} props.rest - Additional props to be spread on the element.
 *
 * @returns {JSX.Element} React element representing the library service form slide.
 */
const LibraryNewServiceSlide = ({
  openServiceSlide,
  handleServiceSlideClose,
  idSelected,
  serviceTypes,
  newFormServiceType,
  onSave,
  onDelete,
  tenantId
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLibraryService, setSelectedLibraryService] = useState();
  const [formData, setFormData] = useState(initialDaysData);
  const [showDelete, setShowDelete] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  // Prevents same multiple API calls
  const hasFetchedRef = useRef(false);

  const handleClose = () => {
    setSelectedLibraryService({
      contenttypeid: 0,
      title: '',
      description: '',
      note: '',
      images: []
    });
    handleServiceSlideClose();
  };

  const onChange = (serviceFieldType, value, additionalData = {}) => {
    if (serviceFieldType?.servicefieldid) {
      const field = formData[serviceFieldType.servicefieldid];
      field.error = null;

      if (Array.isArray(value)) {
        field.options = value.filter((opt) => !!opt.optionId);
      } else {
        field.options[0].value = value;
      }
      const defaultOptions = getDefaultFieldOptions(
        selectedLibraryService,
        serviceFieldType.servicefieldid
      );
      setFormData((prevFormData) => ({
        ...prevFormData,
        [serviceFieldType.servicefieldid]: {
          ...field,
          ...additionalData,
          options: field.options.map((option) => {
            return {
              ...option,
              ...defaultOptions[option.servicefieldoptionid]
            };
          })
        }
      }));
    }
  };

  const onCancelLibraryService = () => {
    setSelectedLibraryService(null);
    setFormData(initialDaysData);
    handleServiceSlideClose();
  };

  const onSelectLibraryService = useCallback(
    async (serviceType) => {
      // If there is a selected serviceType we should not update the form
      if (selectedLibraryService || !tenantId) {
        return;
      }

      //Find original service type data
      const foundLibraryService = serviceTypes?.find(
        (service) =>
          service?.servicetypeid === serviceType?.servicetype?.servicetypeid
      );

      //Look for existing fields from setting that are not in the form
      const missingFields = foundLibraryService?.servicetypefield?.filter(
        (fieldFromSetting) =>
          !serviceType.servicefields.some(
            (formField) =>
              formField.servicefieldid === fieldFromSetting.servicefieldid
          )
      );

      //Map missing fields
      const mappedMissingFields = missingFields?.map((field) => ({
        isvisible: true,
        options: [
          {
            servicefieldoptionid: null,
            value: null,
            tripdetaileddayservicevalueid: null
          }
        ],
        servicefieldid: field?.servicefieldid
      }));

      // Map the service fields by their IDs for quick access
      const serviceFieldsMap = [
        ...serviceType.servicefields,
        ...mappedMissingFields
      ].reduce((acc, sField) => {
        acc[sField.servicefieldid] = sField;
        return acc;
      }, {});

      const fieldTypesMap = {};

      // Format service type fields by combining service fields with their respective types
      const formattedLibraryServiceFields = serviceTypes.reduce(
        (acc, sType) => {
          if (sType.servicetypeid === serviceType.servicetype.servicetypeid) {
            sType.servicetypefield.map((sTField) => {
              fieldTypesMap[sTField.servicefieldid] = sTField.servicefieldtype;
              acc.push({
                ...sTField,
                ...serviceFieldsMap[sType.servicefieldid]
              });
              return sTField;
            });
          }
          return acc;
        },
        []
      );

      // Format the selected service type with updated service fields
      const formattedLibraryService = {
        ...{
          ...serviceType,
          servicefields: [...serviceType.servicefields, ...mappedMissingFields]
        },
        servicetypefield: formattedLibraryServiceFields,
        servicetypeid: serviceType.servicetype.servicetypeid
      };

      // Update the state with the formatted service type and form data
      const newFormData = await setServiceTypeDayFormData(
        formattedLibraryService,
        fieldTypesMap,
        { tenantId },
        true
      );

      setSelectedLibraryService(formattedLibraryService);
      setFormData(newFormData);
      setIsLoading(false);
      setShowDelete(true);
      setIsEditing(true);
    },
    [selectedLibraryService, serviceTypes, tenantId]
  );

  const onError = (servicefieldid, errorMessage = '') => {
    if (servicefieldid) {
      const field = formData[servicefieldid];
      field.error = errorMessage;

      setFormData((prevFormData) => ({
        ...prevFormData,
        [servicefieldid]: {
          ...field
        }
      }));
    }
  };

  const onLibraryServiceChange = (event, serviceType) => {
    setSelectedLibraryService(serviceType);
    setFormData(initServiceTypeDayFormData(serviceType));
    setShowDelete(false);
    setIsEditing(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (hasFetchedRef.current) return;

      hasFetchedRef.current = true;
      setIsLoading(true);
      // If there is no service selected, lets open a new form.
      if (!idSelected || !openServiceSlide) {
        setSelectedLibraryService(newFormServiceType);
        setFormData(initServiceTypeDayFormData(newFormServiceType));
        setIsLoading(false);
        return;
      }

      try {
        const selectedLibraryService = await getLibraryService(idSelected);
        await onSelectLibraryService(selectedLibraryService.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [
    idSelected,
    openServiceSlide,
    onSelectLibraryService,
    newFormServiceType,
    selectedLibraryService
  ]);

  // Reset the fetch flag when the slide closes
  useEffect(() => {
    if (!openServiceSlide) {
      hasFetchedRef.current = false;
    }
  }, [openServiceSlide]);

  if (!selectedLibraryService) return;

  return (
    <Slide
      className='library_new_service_slide'
      direction='left'
      in={openServiceSlide}
      mountOnEnter
      unmountOnExit
    >
      <Box id='content__slide__container'>
        <div className='library_new_service_slide_header'>
          <h2 className='h2-semibold'>
            {idSelected !== 0 ? t('library:editContent') : t('library:newItem')}
          </h2>
          <X size={35} onClick={handleClose} className='library-slide-close' />
        </div>
        <div className='library_new_service_slide_body'>
          <ServiceTypesForm
            id='library-itinerary-service-types'
            serviceType={selectedLibraryService}
            onChange={onChange}
            formData={formData}
            serviceTypes={serviceTypes}
            isLoading={isLoading}
            onCancel={onCancelLibraryService}
            onError={onError}
            onSave={onSave}
            onDelete={onDelete}
            onServiceTypeChange={onLibraryServiceChange}
            updateFormData={setFormData}
            canDelete={showDelete}
            isEditing={isEditing}
            isLibrary
          />
        </div>
      </Box>
    </Slide>
  );
};

LibraryNewServiceSlide.propTypes = {
  openServiceSlide: PropTypes.bool.isRequired,
  tenantId: PropTypes.number.isRequired,
  handleServiceSlideClose: PropTypes.func,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  serviceTypes: PropTypes.array,
  idSelected: PropTypes.number,
  handleDelete: PropTypes.func,
  newFormServiceType: PropTypes.object
};

export default LibraryNewServiceSlide;
